<template>
  <b-card class="wallet-summary-card">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <div>
        <h5 class="mb-0">
          Resumen de monedero
        </h5>
        <div class="text-muted small">
          Vista global para administración
        </div>
      </div>

      <b-button
        size="sm"
        variant="outline-primary"
        @click="$emit('refresh')"
      >
        <b-spinner
          v-if="loading"
          small
          class="mr-1"
        />
        Refrescar
      </b-button>
    </div>

    <div class="wallet-summary-grid">
      <div class="wallet-stat stat-balance">
        <div class="stat-label">
          Saldo total
        </div>
        <div class="stat-value text-success">
          {{ money(totalBalance) }}
        </div>
        <div class="stat-meta">
          Total de monederos: {{ formatCount(totalWallets) }}
        </div>
      </div>
      <div class="wallet-stat">
        <div class="stat-label">
          Monederos
        </div>
        <div class="stat-value">
          {{ formatCount(totalWallets) }}
        </div>
        <div class="stat-meta">
          Clientes con wallet activa
        </div>
      </div>
      <div class="wallet-stat">
        <div class="stat-label">
          Movimientos totales
        </div>
        <div class="stat-value">
          {{ formatCount(totalMovements) }}
        </div>
        <div class="stat-meta">
          Créditos, débitos y ajustes
        </div>
      </div>
    </div>

    <div class="wallet-kpi-grid">
      <div class="wallet-kpi kpi-success">
        <div class="kpi-label">
          Abonos
        </div>
        <div class="kpi-value text-success">
          {{ money(creditsAmount) }}
        </div>
        <b-badge variant="light-success">
          Movimientos: {{ formatCount(counts.credit) }}
        </b-badge>
      </div>
      <div class="wallet-kpi kpi-danger">
        <div class="kpi-label">
          Egresos
        </div>
        <div class="kpi-value text-danger">
          {{ money(debitsAmount) }}
        </div>
        <b-badge variant="light-danger">
          Movimientos: {{ formatCount(counts.debit) }}
        </b-badge>
      </div>
      <div class="wallet-kpi kpi-warning">
        <div class="kpi-label">
          Ajustes netos
        </div>
        <div
          class="kpi-value"
          :class="adjustmentsNetClass"
        >
          {{ money(adjustmentsNet) }}
        </div>
        <b-badge variant="light-warning">
          Movimientos: {{ formatCount(counts.adjustment) }}
        </b-badge>
      </div>
    </div>
  </b-card>
</template>

<script>
export default {
  name: 'WalletAdminSummaryCard',
  props: {
    summary: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    totals() {
      return (this.summary && this.summary.totals) || {}
    },
    counts() {
      const base = (this.totals && this.totals.counts) || {}
      const credit = Number(base.credit || 0)
      const debit = Number(base.debit || 0)
      const adjustment = Number(base.adjustment || 0)
      const baseSum = credit + debit + adjustment
      if (baseSum > 0) {
        return { credit, debit, adjustment }
      }

      const txs = (this.summary && this.summary.transactions) || []
      if (!Array.isArray(txs) || txs.length === 0) {
        return { credit, debit, adjustment }
      }

      const agg = { credit: 0, debit: 0, adjustment: 0 }
      txs.forEach(t => {
        if (t.tx_type === 'credit') agg.credit += 1
        else if (t.tx_type === 'debit') agg.debit += 1
        else if (t.tx_type === 'adjustment') agg.adjustment += 1
      })
      return agg
    },
    totalBalance() {
      return Number((this.summary && this.summary.total_balance) || 0)
    },
    totalWallets() {
      return Number((this.summary && this.summary.total_wallets) || 0)
    },
    creditsAmount() {
      return Number((this.totals && this.totals.credits_amount) || 0)
    },
    debitsAmount() {
      return Number((this.totals && this.totals.debits_amount) || 0)
    },
    adjustmentsNet() {
      return Number((this.totals && this.totals.adjustments_net) || 0)
    },
    adjustmentsNetClass() {
      if (this.adjustmentsNet > 0) return 'text-success'
      if (this.adjustmentsNet < 0) return 'text-danger'
      return 'text-warning'
    },
    totalMovements() {
      const countsSum = Number(this.counts.credit || 0)
        + Number(this.counts.debit || 0)
        + Number(this.counts.adjustment || 0)
      if (countsSum > 0) return countsSum

      const meta = (this.summary && this.summary.meta) || {}
      const txMeta = meta.transactions_pagination || {}
      if (txMeta.total_objects != null && Number(txMeta.total_objects) > 0) {
        return Number(txMeta.total_objects)
      }

      const txs = (this.summary && this.summary.transactions) || []
      return Array.isArray(txs) ? txs.length : countsSum
    },
  },
  methods: {
    money(v) {
      const n = Number(v || 0)
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n)
    },
    formatCount(v) {
      return new Intl.NumberFormat('es-MX').format(Number(v || 0))
    },
  },
}
</script>

<style scoped>
.wallet-summary-card {
  border: 1px solid #edf0f4;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 55%, #f1f5f9 100%);
  box-shadow: 0 12px 30px rgba(17, 38, 63, 0.06);
  overflow: hidden;
  position: relative;
}

.wallet-summary-card::after {
  content: '';
  position: absolute;
  top: -120px;
  right: -120px;
  width: 240px;
  height: 240px;
  background: radial-gradient(circle, rgba(76, 175, 80, 0.18), rgba(76, 175, 80, 0));
  pointer-events: none;
}

.wallet-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.wallet-stat {
  background: #ffffff;
  border: 1px solid #e9eef3;
  border-radius: 12px;
  padding: 14px 16px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.wallet-stat:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 18px rgba(17, 38, 63, 0.08);
}

.stat-label {
  font-size: 0.8rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.stat-value {
  font-size: 1.4rem;
  font-weight: 700;
}

.stat-meta {
  font-size: 0.8rem;
  color: #98a2b3;
}

.wallet-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.wallet-kpi {
  background: #ffffff;
  border: 1px solid #e9eef3;
  border-radius: 12px;
  padding: 12px 16px;
}

.kpi-label {
  font-size: 0.8rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.kpi-value {
  font-size: 1.1rem;
  font-weight: 700;
}
</style>
